import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import DetailsBox from '../../../components/Cards/DetailsBox'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import moment from 'moment'
import API from '../../../services/API'
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  ButtonGroup,
  IconButton,
  LinearProgress,
  Modal,
  Paper,
  TextField,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTheme } from '@emotion/react'
import SmallModal from '../../../components/Modals/SmallModal'
import { GlobalContext } from '../../../App'
import useIsMobile from '../../../helpers/useIsMobile'

const SelectedApplication = ({ application, loadApplications, onClose }) => {
  const [loading, setLoading] = useState(true)
  const [otherApplications, setOtherApplications] = useState([])
  const [employeeConflicts, setEmployeeConflicts] = useState([])
  const [confirmAcceptPopup, setConfirmAcceptPopup] = useState(false)
  const [confirmRejectPopup, setConfirmRejectPopup] = useState(false)
  const [rejectedReason, setRejectedReason] = useState('')
  const [reviewError, setReviewError] = useState(null)
  const [submittingReview, setSubmittingReview] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const isMobile = useIsMobile('sm')

  const handleReviewApplication = (status) => {
    setSubmittingReview(true)
    setReviewError(null)

    API.post(
      '/shift-request-review',
      {
        applicationID: application._id,
        otherApplications:
          status === 'Accepted' &&
          otherApplications.reduce(
            (accumulator, app) =>
              accumulator.push({
                application: app._id,
                employee: app.employee,
              }) && accumulator,
            [],
          ),
        status,
        rejectedReason,
        employeeID: application.employee.userAccount._id,
      },
      { skipGlobalErrorHandler: true },
    )
      .then((res) => {
        if (res.status === 200) {
          setConfirmAcceptPopup(false)
          setConfirmRejectPopup(false)
          setReviewError(null)
          onClose()
          loadApplications()
          setToast({
            message:
              status === 'Accepted'
                ? 'Shift Request Accepted'
                : status === 'Rejected' && 'Shift Rejected',
            severity: 'success',
          })
        } else if (
          res.status === 400 &&
          res.data?.errorCode === 'shiftAssignmentBlocked'
        ) {
          setReviewError({
            title: 'Shift cannot be approved',
            message:
              'This application cannot be approved until the following issue is resolved.',
            issues: res.data?.issues ?? [],
          })
        } else {
          setReviewError({
            title: 'Unable to review application',
            message:
              res.data?.error ||
              res.data?.message ||
              'Unable to perform action. Please try again later.',
          })
        }
      })
      .catch((error) => {
        setReviewError({
          title: 'Unable to review application',
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            error?.message ||
            'Unable to perform action. Please try again later.',
        })
      })
      .finally(() => setSubmittingReview(false))
  }

  useEffect(() => {
    API.get(
      '/conflicting-applications?shiftID=' +
        application.shift._id +
        '&applicationID=' +
        application._id,
    )
      .then((res) => {
        setOtherApplications(res.data.otherApplications)
        setEmployeeConflicts(res.data.employeeConflicts)
      })
      .finally(() => setLoading(false))
  }, [])

  return (
    <Stack
      sx={{ width: { xs: '100vw', sm: 500 }, maxWidth: '100vw', boxSizing: 'border-box', p: 1 }}
    >
      {loading && <LinearProgress />}
      <DetailsBox
        title={`Application for ${application?.employee.userAccount?.firstName} ${application?.employee.userAccount?.lastName}`}
        buttonRight={
          isMobile && (
            <IconButton aria-label="Close" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )
        }
        rows={[
          {
            label: application?.shift?.event ? 'Event' : 'Type',
            value: application?.shift?.event ? (
              <Link
                to={`/events/${application?.shift.event._id}`}
                target="_blank"
              >
                {application?.shift.event.title}
              </Link>
            ) : (
              application?.shift?.shiftType
            ),
            fullWidth: true,
          },
          {
            label: 'Start',
            value: moment(application?.shift.start).format('Do MMM - HH:mm'),
          },
          {
            label: 'End',
            value: moment(application?.shift.end).format('Do MMM - HH:mm'),
          },
          {
            label: 'Clinical Role',
            value: application?.shift.clinicalRole,
          },
          ...(application?.shift?.eventAssignment
            ? [
                {
                  label: 'Event Role',
                  value: application.shift.eventAssignment,
                },
              ]
            : []),
          {
            label: 'Application Date',
            value: moment(application?.createdAt)
              .format('Do MMM YYYY HH:mm')
              .toString(),
            fullWidth: true,
          },
        ]}
      >
        {!loading && (
          <Stack gap={1}>
            {application?.shift?.assignedTo &&
              application?.shift?.availableForReassignment && (
                <Alert severity="info">
                  This shift is currently assigned to{' '}
                  {application.shift.assignedTo.userAccount.firstName}{' '}
                  {application.shift.assignedTo.userAccount.lastName}, but they
                  have offered it out for reallocation. Approving this shift
                  application will remove{' '}
                  {application.shift.assignedTo.userAccount.firstName} from
                  their shift.
                </Alert>
              )}
            {otherApplications.length === 0 ? (
              <Alert severity="success">
                No other applications for this shift
              </Alert>
            ) : (
              <Alert
                severity="warning"
                icon={false}
                sx={{ border: 'solid 1px' }}
              >
                <AlertTitle>
                  The following staff have also applied for this shift:
                </AlertTitle>
                <ul>
                  {otherApplications.map((o) => (
                    <li key={o._id}>
                      {o.employee.userAccount.firstName +
                        ' ' +
                        o.employee.userAccount.lastName}
                    </li>
                  ))}
                </ul>
                <Typography
                  variant="caption"
                  fontSize="0.75rem"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Accepting this shift application for{' '}
                  {application?.employee.userAccount?.firstName}{' '}
                  {application?.employee.userAccount?.lastName} will
                  automatically reject it for others
                </Typography>
              </Alert>
            )}
            {employeeConflicts.length === 0 ? (
              <Alert severity="success">
                Employee has no conflicting shifts
              </Alert>
            ) : (
              <Alert severity="error" icon={false} sx={{ border: 'solid 1px' }}>
                <AlertTitle> Staff Member has the following shifts:</AlertTitle>
                <ul>
                  {employeeConflicts.map((c) => (
                    <li key={c._id}>
                      <b
                        onClick={() =>
                          c.event?._id &&
                          window.open(`/events/${c.event._id}`, '_blank')
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {c?.event?.title ?? c?.shiftType}
                      </b>
                      <br />
                      <>
                        {moment(c.start).format('DD/MM/YYYY HH:mm')} -{' '}
                        {moment(c.end).format('DD/MM/YYYY HH:mm')}
                      </>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
            {reviewError && (
              <Alert severity="error">
                <AlertTitle>{reviewError.title}</AlertTitle>
                <Typography variant="body2">{reviewError.message}</Typography>
                {reviewError.issues?.length > 0 && (
                  <ul>
                    {reviewError.issues.map((issue, index) => (
                      <li key={`${issue.code}-${index}`}>{issue.message}</li>
                    ))}
                  </ul>
                )}
              </Alert>
            )}
            <ButtonGroup fullWidth variant="contained">
              <Button
                onClick={() => {
                  setReviewError(null)
                  setConfirmAcceptPopup(true)
                }}
                color="success"
                disabled={submittingReview}
              >
                Approve
              </Button>
              <Button
                onClick={() => {
                  setReviewError(null)
                  setConfirmRejectPopup(true)
                }}
                color="error"
                disabled={submittingReview}
              >
                Reject
              </Button>
            </ButtonGroup>
          </Stack>
        )}
      </DetailsBox>
      <Modal
        open={confirmAcceptPopup || confirmRejectPopup}
        onClose={() => {
          setConfirmAcceptPopup(false)
          setConfirmRejectPopup(false)
        }}
      >
        <Alert
          severity="info"
          icon={false}
          onClose={() => {
            setConfirmAcceptPopup(false)
            setConfirmRejectPopup(false)
          }}
        >
          <AlertTitle>
            Are you sure you want to{' '}
            {confirmAcceptPopup ? 'accept' : confirmRejectPopup && 'reject'}{' '}
            this shift request?
          </AlertTitle>
          <Stack
            gap={1}
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              confirmAcceptPopup
                ? handleReviewApplication('Accepted')
                : confirmRejectPopup && handleReviewApplication('Rejected')
            }}
          >
            {confirmRejectPopup && (
              <Autocomplete
                freeSolo
                options={[
                  'Staff no longer available',
                  'Event Cancelled',
                  'Shift already assigned',
                ]}
                value={rejectedReason}
                inputValue={rejectedReason}
                onChange={(e, value) => setRejectedReason(value || '')}
                onInputChange={(e, value) => setRejectedReason(value)}
                renderInput={(props) => (
                  <TextField required {...props} label="Reason" size="small" />
                )}
              />
            )}
            {confirmAcceptPopup &&
              'This will reject any other requests for the same shift, and ignore any conflicts.'}
            {reviewError && (
              <Alert severity="error">
                <AlertTitle>{reviewError.title}</AlertTitle>
                <Typography variant="body2">{reviewError.message}</Typography>
                {reviewError.issues?.length > 0 && (
                  <ul>
                    {reviewError.issues.map((issue, index) => (
                      <li key={`${issue.code}-${index}`}>{issue.message}</li>
                    ))}
                  </ul>
                )}
              </Alert>
            )}
            <ButtonGroup size="small" variant="outlined" fullWidth>
              <Button
                color="success"
                type="submit"
                disabled={submittingReview}
              >
                {submittingReview ? 'Working...' : 'Yes'}
              </Button>
              <Button
                color="error"
                disabled={submittingReview}
                onClick={() => {
                  setConfirmAcceptPopup(false)
                  setConfirmRejectPopup(false)
                  setReviewError(null)
                }}
              >
                No
              </Button>
            </ButtonGroup>
          </Stack>
        </Alert>
      </Modal>
    </Stack>
  )
}

export default SelectedApplication
